import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowRight, CheckSquare, Square } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const defaultStep = (n) => ({
  subject: n === 0 ? '' : 'Re: {{subject}}',
  body: '',
  waitDays: n === 0 ? 0 : n === 1 ? 3 : 7,
  type: n === 0 ? 'initial' : 'followup',
});

export default function NewCampaign() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [brevoAccounts, setBrevoAccounts] = useState([]);
  const [selectedBrevoAccounts, setSelectedBrevoAccounts] = useState([]);
  const [sendingMethod, setSendingMethod] = useState('gmail');
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(null);
  const [form, setForm] = useState({
    name: '', listId: '', batchLimit: 100, delaySeconds: 60,
    trackOpens: false, trackClicks: false, testEmail: '',
  });
  const [steps, setSteps] = useState([defaultStep(0), defaultStep(1)]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(0);

  useEffect(() => {
    api.get('/api/leads/lists').then(r => setLists(r.data.lists || []));
    api.get('/api/accounts').then(r => {
      const active = r.data.accounts?.filter(a => a.is_active) || [];
      setAccounts(active);
      setSelectedAccounts(active.map(a => a.id));
    });
    api.get('/api/brevo').then(r => {
      const active = r.data.accounts?.filter(a => a.is_active) || [];
      setBrevoAccounts(active);
      setSelectedBrevoAccounts(active.map(a => a.id));
    }).catch(() => {});
    api.get('/api/templates').then(r => setTemplates(r.data.templates || []));
  }, []);

  const applyTemplate = (stepIndex, template) => {
    updateStep(stepIndex, 'subject', template.subject);
    updateStep(stepIndex, 'body', template.body);
    setShowTemplates(null);
    toast.success('Template applied!');
  };

  const toggleAccount = (id) => setSelectedAccounts(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  const toggleBrevoAccount = (id) => setSelectedBrevoAccounts(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  const selectAll = () => setSelectedAccounts(accounts.map(a => a.id));
  const deselectAll = () => setSelectedAccounts([]);

  const addStep = () => setSteps(s => [...s, defaultStep(s.length)]);
  const removeStep = (i) => setSteps(s => s.filter((_, idx) => idx !== i));
  const updateStep = (i, key, val) => setSteps(s => s.map((step, idx) => idx === i ? { ...step, [key]: val } : step));

  const vars = ['{{first_name}}', '{{last_name}}', '{{email}}', '{{company}}'];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.listId) return toast.error('Please select a lead list');
    if (sendingMethod === 'gmail' && selectedAccounts.length === 0) return toast.error('Please select at least one Gmail account');
    if (sendingMethod === 'brevo' && selectedBrevoAccounts.length === 0) return toast.error('Please select at least one Brevo account');
    if (!steps[0].subject || !steps[0].body) return toast.error('Initial email needs subject and body');
    setLoading(true);
    try {
      const res = await api.post('/api/campaigns', {
        ...form,
        batchLimit: parseInt(form.batchLimit),
        delaySeconds: parseInt(form.delaySeconds),
        selectedAccountIds: sendingMethod === 'gmail' ? selectedAccounts : null,
        brevoAccountIds: sendingMethod === 'brevo' ? selectedBrevoAccounts : null,
        sendingMethod,
        testEmail: form.testEmail || null,
        sequence: {
          name: form.name + ' sequence',
          stopOnReply: true,
          skipWeekends: false,
          sendHourStart: 0,
          sendHourEnd: 24,
          timezone: 'Africa/Lagos',
          steps,
        },
      });
      toast.success('Campaign created!');
      navigate(`/campaigns/${res.data.campaign.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create campaign');
    } finally { setLoading(false); }
  };

  const stepColor = (i) => i === 0 ? 'var(--blue)' : i === steps.length - 1 && steps.length > 2 ? '#ec4899' : 'var(--amber)';
  const stepLabel = (i) => i === 0 ? 'Initial email' : i === steps.length - 1 && steps.length > 2 ? 'Breakup email' : `Follow-up ${i}`;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>New campaign</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Set up your campaign and pick which accounts to send from</p>
      </div>

      <form onSubmit={submit}>
        {/* Campaign basics */}
        <div className="card" style={{ padding: 22, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 16 }}>Campaign details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Campaign name</label>
              <input className="input" placeholder="Q3 Outreach" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Lead list</label>
              <select className="input" value={form.listId} onChange={e => setForm({ ...form, listId: e.target.value })} required>
                <option value="">Select a list…</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({parseInt(l.lead_count) || 0} leads)</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Switch account after</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="input" type="number" min={1} max={500} value={form.batchLimit} onChange={e => setForm({ ...form, batchLimit: e.target.value })} />
                <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>emails</span>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Delay between emails</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="input" type="number" min={5} max={300} value={form.delaySeconds} onChange={e => setForm({ ...form, delaySeconds: e.target.value })} />
                <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>seconds</span>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
                Test email <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional — gets a copy before launch + notified on every account switch)</span>
              </label>
              <input className="input" type="email" placeholder="youremail@gmail.com"
                value={form.testEmail} onChange={e => setForm({ ...form, testEmail: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Sending method */}
        <div className="card" style={{ padding: 22, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>Sending method</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div onClick={() => setSendingMethod('gmail')} style={{ padding: '14px 16px', borderRadius: 'var(--radius-sm)', border: `2px solid ${sendingMethod === 'gmail' ? 'var(--blue)' : 'var(--border)'}`, background: sendingMethod === 'gmail' ? 'var(--blue-glow)' : 'var(--bg3)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: sendingMethod === 'gmail' ? 'var(--blue)' : 'var(--border)' }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Gmail</span>
                <span className="badge badge-gray" style={{ fontSize: 10 }}>150/day per account</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>Send via your linked Gmail accounts. Rotate between multiple accounts automatically.</div>
            </div>
            <div onClick={() => setSendingMethod('brevo')} style={{ padding: '14px 16px', borderRadius: 'var(--radius-sm)', border: `2px solid ${sendingMethod === 'brevo' ? 'var(--amber)' : 'var(--border)'}`, background: sendingMethod === 'brevo' ? 'rgba(245,158,11,0.08)' : 'var(--bg3)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: sendingMethod === 'brevo' ? 'var(--amber)' : 'var(--border)' }} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Brevo</span>
                <span className="badge badge-amber" style={{ fontSize: 10 }}>Better inbox delivery</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>Send via Brevo API. Much better deliverability — emails land in inbox not spam.</div>
            </div>
          </div>
        </div>

        {/* Gmail account selector */}
        {sendingMethod === 'gmail' && (
          <div className="card" style={{ padding: 22, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Select Gmail accounts</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                  {selectedAccounts.length} of {accounts.length} selected
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={selectAll}>Select all</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={deselectAll}>Deselect all</button>
              </div>
            </div>

            {accounts.length === 0 ? (
              <div style={{ padding: '14px 0', fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
                No active Gmail accounts linked.{' '}
                <button type="button" onClick={() => navigate('/accounts')} style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 13 }}>
                  Link one here →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {accounts.map((acc, i) => {
                  const isSelected = selectedAccounts.includes(acc.id);
                  return (
                    <div key={acc.id} onClick={() => toggleAccount(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: `1px solid ${isSelected ? 'var(--blue)' : 'var(--border)'}`, background: isSelected ? 'var(--blue-glow)' : 'var(--bg3)', cursor: 'pointer', transition: 'all 0.15s' }}>
                      {isSelected ? <CheckSquare size={16} color="var(--blue)" style={{ flexShrink: 0 }} /> : <Square size={16} color="var(--text3)" style={{ flexShrink: 0 }} />}
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: isSelected ? 'var(--blue-dim)' : 'var(--bg2)', color: isSelected ? '#93c5fd' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                        {acc.email.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: isSelected ? 'var(--text)' : 'var(--text2)' }}>{acc.email}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>Queue #{i + 1} · batch of {form.batchLimit}</div>
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: isSelected ? 'var(--blue-dim)' : 'var(--bg2)', color: isSelected ? '#93c5fd' : 'var(--text3)', flexShrink: 0 }}>
                        {isSelected ? 'selected' : 'skipped'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedAccounts.length > 0 && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text2)' }}>
                <strong style={{ color: 'var(--text)' }}>Sending order:</strong>{' '}
                {accounts.filter(a => selectedAccounts.includes(a.id)).map(a => a.email.split('@')[0]).join(' → ')} → loops back
              </div>
            )}
          </div>
        )}

        {/* Brevo account selector */}
        {sendingMethod === 'brevo' && (
          <div className="card" style={{ padding: 22, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 14 }}>
              Select Brevo accounts
              <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 400, marginLeft: 10 }}>
                {selectedBrevoAccounts.length} selected · {selectedBrevoAccounts.length * 300} emails/day capacity
              </span>
            </div>

            {brevoAccounts.length === 0 ? (
              <div style={{ padding: '14px 0', fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
                No Brevo accounts yet.{' '}
                <button type="button" onClick={() => navigate('/accounts')} style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', fontSize: 13 }}>
                  Add one here →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {brevoAccounts.map((acc, i) => {
                  const isSelected = selectedBrevoAccounts.includes(acc.id);
                  return (
                    <div key={acc.id} onClick={() => toggleBrevoAccount(acc.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: `1px solid ${isSelected ? 'var(--amber)' : 'var(--border)'}`, background: isSelected ? 'rgba(245,158,11,0.08)' : 'var(--bg3)', cursor: 'pointer', transition: 'all 0.15s' }}>
                      {isSelected ? <CheckSquare size={16} color="var(--amber)" style={{ flexShrink: 0 }} /> : <Square size={16} color="var(--text3)" style={{ flexShrink: 0 }} />}
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: isSelected ? 'rgba(245,158,11,0.2)' : 'var(--bg2)', color: isSelected ? 'var(--amber)' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                        {acc.sender_email.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{acc.sender_email}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{acc.sender_name} · 300/day · Queue #{i + 1}</div>
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: isSelected ? 'rgba(245,158,11,0.2)' : 'var(--bg2)', color: isSelected ? 'var(--amber)' : 'var(--text3)', flexShrink: 0 }}>
                        {isSelected ? 'selected' : 'skipped'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Sequence steps */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, color: 'var(--text2)' }}>Email sequence</div>
          {steps.map((step, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              {i > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 16px', marginBottom: 10 }}>
                  <div style={{ width: 1, height: 28, background: 'var(--border)', margin: '0 9px' }} />
                  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 12px', fontSize: 11, color: 'var(--text3)' }}>
                    wait {step.waitDays} day{step.waitDays !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
              <div className="card" style={{ borderLeft: `3px solid ${stepColor(i)}` }}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setExpanded(expanded === i ? -1 : i)}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: stepColor(i), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 600, flexShrink: 0 }}>{i + 1}</div>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{stepLabel(i)}</span>
                  {i > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>wait</span>
                      <select className="input" style={{ width: 80, padding: '3px 8px', fontSize: 12 }}
                        value={step.waitDays}
                        onChange={e => { e.stopPropagation(); updateStep(i, 'waitDays', parseInt(e.target.value)); }}
                        onClick={e => e.stopPropagation()}>
                        {[1,2,3,5,7,10,14].map(d => <option key={d} value={d}>{d}d</option>)}
                      </select>
                    </div>
                  )}
                  {steps.length > 1 && i > 0 && (
                    <button type="button" onClick={e => { e.stopPropagation(); removeStep(i); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', padding: 4 }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                  {expanded === i ? <ChevronUp size={14} color="var(--text3)" /> : <ChevronDown size={14} color="var(--text3)" />}
                </div>

                {expanded === i && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
                    {/* Template picker */}
                    {templates.length > 0 && (
                      <div style={{ marginTop: 14, marginBottom: 4 }}>
                        <button type="button" className="btn btn-ghost btn-sm"
                          onClick={() => setShowTemplates(showTemplates === i ? null : i)}
                          style={{ fontSize: 12, color: 'var(--blue)' }}>
                          📄 Use a saved template
                        </button>
                        {showTemplates === i && (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 8, background: 'var(--bg3)' }}>
                            {templates.map(t => (
                              <div key={t.id} onClick={() => applyTemplate(i, t)} style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</div>
                                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Subject: {t.subject.slice(0, 60)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Subject</label>
                      <input className="input" placeholder="Hey {{first_name}}, quick question" value={step.subject} onChange={e => updateStep(i, 'subject', e.target.value)} required={i === 0} />
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <label style={{ fontSize: 12, color: 'var(--text2)' }}>Body</label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {vars.map(v => (
                            <button key={v} type="button" onClick={() => updateStep(i, 'body', step.body + v)}
                              style={{ fontSize: 10, padding: '2px 7px', background: 'var(--blue-dim)', color: '#93c5fd', border: 'none', borderRadius: 20, cursor: 'pointer' }}>
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea className="input" rows={6} placeholder="Write your email here…" style={{ resize: 'vertical', lineHeight: 1.6 }}
                        value={step.body} onChange={e => updateStep(i, 'body', e.target.value)} required={i === 0} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 4, borderStyle: 'dashed' }} onClick={addStep}>
            <Plus size={14} /> Add follow-up step
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/campaigns')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating…' : 'Create campaign'} <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}










